/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PaymentFormSkeleton from '@features/subscription/components/PaymentFormSkeleton';

/** What the framed form is doing, as it reports it. */
export type PayienFormState = 'idle' | 'submitting' | 'error';

/**
 * Height the frame falls back to before (or without) a `checkout_resize`
 * report. The hosted card form is a tall, single-column layout — this is
 * enough to show every field plus the security note without an inner
 * scrollbar. The frame reveals at this height the instant it loads, and a
 * resize message (when one arrives) only refines the fit.
 *
 * Why a fallback at all: `checkout_resize` is posted cross-origin to the
 * success_url / framer origin, and a host-url/stage mismatch (or a browser
 * that leaves `ancestorOrigins`/`document.referrer` empty for this
 * sandboxed, `allow-same-origin`-less frame) makes the browser silently drop
 * it. The visibility of the card fields must not hang on a message that may
 * never be delivered — that was the "Trocar cartão" perpetual-skeleton bug.
 */
const DEFAULT_FRAME_HEIGHT = 560;

interface PaymentMethodIframeProps {
  formUrl: string;
  onSuccess: () => void;
  onCancel: () => void;
  onExpired?: () => void;
  /**
   * Bump this to ask the framed form to submit.
   *
   * The host owns the footer now, so "Salvar novo cartão" lives outside the
   * iframe. A counter rather than a boolean: two submissions in a row are two
   * distinct asks, and a flag that is already true says nothing the second
   * time.
   */
  submitSignal?: number;
  /** Told whenever the framed form changes state, so the host can disable its button. */
  onStateChange?: (state: PayienFormState, message?: string) => void;
}

/**
 * Embeds Payien's hosted, PCI-compliant card-update form.
 *
 * Meant to be rendered inside the shared `Modal` component, which already
 * provides the dialog chrome (focus trap, Escape-to-close, backdrop, ARIA)
 * via Radix — this component only owns the iframe and its postMessage
 * contract.
 *
 * Listens for `payment_method_*` messages and validates `event.origin`
 * against the iframe's own origin before acting on any message — without
 * that check, any other frame/script on the page could spoof a fake success
 * message and make the UI believe the card changed when it didn't.
 */
const PaymentMethodIframe: React.FC<PaymentMethodIframeProps> = ({
  formUrl,
  onSuccess,
  onCancel,
  onExpired,
  submitSignal,
  onStateChange,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { t } = useTranslation('features/pricing');
  const [isFrameLoaded, setIsFrameLoaded] = useState(false);
  /** Reported content height. Until it arrives the frame falls back to
   *  `DEFAULT_FRAME_HEIGHT` so the card fields are visible and usable the
   *  moment the frame loads — the resize report only refines the fit. */
  const [frameHeight, setFrameHeight] = useState<number | null>(null);

  const expectedOrigin = useMemo(() => {
    try {
      return new URL(formUrl).origin;
    } catch {
      return null;
    }
  }, [formUrl]);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object') return;
      // A sandbox without allow-same-origin gives the framed page an *opaque*
      // origin, so every message it sends arrives as the string "null" — and
      // comparing that to Payien's origin dropped all of them. The frame then
      // never resized and, worse, its success never arrived: the modal stayed
      // open with the button spinning while the card had already been changed.
      //
      // Keeping the sandbox is the right call (see the iframe below), so the
      // source check is what carries the weight here: only this frame's own
      // contentWindow passes it, whatever origin it claims.
      const originAllowed =
        !expectedOrigin || event.origin === expectedOrigin || event.origin === 'null';
      if (!originAllowed) return;
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;

      const { type, height } = event.data as { type?: string; height?: number };

      if (type === 'checkout_resize' && typeof height === 'number' && height > 0 && iframeRef.current) {
        // The report refines the fit. Apply it to the live element and mirror
        // it in state, so a later re-render keeps the reported height rather
        // than snapping back to the default fallback.
        iframeRef.current.style.height = `${height}px`;
        setFrameHeight(height);
      } else if (type === 'payment_method_success') {
        onSuccess();
      } else if (type === 'payment_method_cancel') {
        onCancel();
      } else if (type === 'session_expired') {
        onExpired?.();
      } else if (type === 'payien_state') {
        const { state, message } = event.data as { state?: PayienFormState; message?: string };
        if (state) onStateChange?.(state, message);
      }
    },
    [expectedOrigin, onSuccess, onCancel, onExpired, onStateChange],
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  // Ask the framed form to submit. The sandbox omits allow-same-origin, so the
  // framed document has an opaque origin. A message aimed at Payien's real
  // origin is discarded, and the string "null" is rejected as a targetOrigin.
  // "*" is the target the browser will deliver to this contentWindow. The
  // body is only the submit request; the form checks the sender before it
  // sends the card.
  useEffect(() => {
    if (!submitSignal || !expectedOrigin) return;
    iframeRef.current?.contentWindow?.postMessage({ type: 'payien_submit' }, '*');
  }, [submitSignal, expectedOrigin]);

  const shown = isFrameLoaded;
  const effectiveHeight = frameHeight ?? DEFAULT_FRAME_HEIGHT;

  return (
    <div className="relative">
      {/* The framed page is a third-party round trip: on a slow link the
          iframe paints nothing for a beat. The placeholder stays in flow
          until the frame has loaded, so the dialog never holds a fixed empty
          band. Once loaded, the fields show at a sensible default height and
          a `checkout_resize` message (if one arrives) only refines the fit —
          the fields never wait on that message to become visible. */}
      {!shown && <PaymentFormSkeleton />}
      <iframe
        ref={iframeRef}
        src={formUrl}
        title={t('subscription.payment.iframeTitle')}
        onLoad={() => setIsFrameLoaded(true)}
        // allow-same-origin is intentionally omitted: Payien's form never
        // needs to read/write anything on its own origin's storage, and
        // pairing it with allow-scripts would let the framed page strip its
        // own sandbox. Top navigation is not granted either — success,
        // cancel, and expiry are all reported via postMessage, so the framed
        // page never needs to navigate this tab.
        sandbox="allow-forms allow-scripts allow-popups"
        scrolling="no"
        className="w-full"
        style={{
          border: 'none',
          display: 'block',
          // Kept laid out while hidden: display:none would give the form a
          // zero width, and the height it reported would be the stacked
          // phone layout. Absolute takes it out of flow so the skeleton
          // holds the space.
          position: shown ? 'static' : 'absolute',
          inset: shown ? undefined : 0,
          height: effectiveHeight,
          minHeight: 0,
          opacity: shown ? 1 : 0,
          pointerEvents: shown ? 'auto' : 'none',
        }}
        data-testid="payment-method-iframe"
      />
    </div>
  );
};

export default PaymentMethodIframe;
